import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { previewAPI } from '../lib/api';
import { useTranslation } from '../lib/i18n';
import { isAuthenticated } from '../lib/auth';
import { track } from '../lib/analytics';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import {
  Loader2, CheckCircle2, XCircle, Clock, Play, Pause, AlertCircle,
  LayoutDashboard, Settings, LogIn, Save, RefreshCw,
} from 'lucide-react';
import PerPlatformCaptionEditor from '../components/PerPlatformCaptionEditor';

/**
 * `/preview?token=<jwt>&action=<approve|reject>?`. Public landing page
 * for the buttons in the auto-post preview email.
 *
 * Three operating modes, decided on mount:
 *   - `?action=approve` → call approve, show success
 *   - `?action=reject`  → call reject, show "skipped" confirmation
 *   - no action         → fetch + render the preview (video, caption,
 *                         scheduled time) with two buttons (Bestätigen /
 *                         Abbrechen) so the user can review then act
 *
 * No auth header required. The JWT in the URL is the auth. The token
 * is scoped to ONE queue item, expires after 30h, and only allows the
 * approve/reject ops on that specific item.
 *
 * Idempotent on the server side: clicking approve twice → no-op the
 * second time. We surface the resulting state cleanly so a user clicking
 * "approve" from a stale email sees "already approved" instead of an
 * error.
 */
export default function PreviewPage() {
  const { t, lang } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const action = searchParams.get('action'); // null | 'approve' | 'reject'

  // Auth-aware footer CTAs. The user reaches this page from an email
  // link, which may open in a logged-OUT browser session (different
  // browser, incognito, mobile email client). Show the right path home:
  //   * Logged in  → buttons back to /dashboard and /auto-post
  //   * Logged out → single "Sign in" button to /auth
  // Read auth ONCE at mount; if the user signs in/out during this
  // session the page is going to be closed anyway.
  const loggedIn = useRef(isAuthenticated()).current;

  const [state, setState] = useState('loading'); // loading | viewing | approved | rejected | error | conflict
  const [data, setData] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [busy, setBusy] = useState(false);
  // Working copy of per-platform captions. Lives separately from
  // `data.per_platform_captions` (the server-side snapshot) so the
  // user can edit freely before deciding. Diffed at decision time to
  // auto-save any pending edits before approving/publishing.
  const [editedCaptions, setEditedCaptions] = useState({});
  // True once the user has actually edited a caption in the on-page
  // editor. Distinguishes "captions differ because state lagged"
  // (e.g. auto-action URL param path where the mount-effect setState
  // hasn't flushed yet) from "user actually changed something."
  // Without this guard, hitting `/preview?token=…&action=approve`
  // would trigger a phantom PATCH that wipes captions to {}.
  const userEditedRef = useRef(false);
  const [regenerating, setRegenerating] = useState(false);
  const [savingCaptions, setSavingCaptions] = useState(false);
  // Fast-track edit / different property (#22, #23)
  const [fastTrackOpen, setFastTrackOpen] = useState(false);
  const [options, setOptions] = useState(null);
  const [pickTemplate, setPickTemplate] = useState('');
  const [pickProperty, setPickProperty] = useState('');
  const [pickImages, setPickImages] = useState([]);   // chosen image URLs, in order
  const [editFields, setEditFields] = useState({});    // { fieldKey: value }
  const [editVoiceover, setEditVoiceover] = useState('');
  // Per-render voiceover on/off. Seeded once from the item's current state;
  // NOT re-seeded on combo switches (it's a user preference, not derived
  // from the property/template).
  const [editVoiceoverOn, setEditVoiceoverOn] = useState(true);
  // Background-music pick. '' = automatic (random from the user's liked
  // tracks — the scheduler default). Seeded once from the current render.
  const [pickMusic, setPickMusic] = useState('');
  const [musicPlaying, setMusicPlaying] = useState(false);
  const musicAudioRef = useRef(null);
  const [rerendering, setRerendering] = useState(false);
  // True while the editors are being refreshed for a newly picked
  // property/template combo (AI regenerates the texts, so ~1-3s).
  const [comboLoading, setComboLoading] = useState(false);
  // Monotonic request id so a slow options response for an older combo
  // can't clobber the editors after the user has already switched again.
  const comboReqRef = useRef(0);
  // Synchronous lock around runDecision. The mount-effect calls it
  // when ?action= is present; if the user also taps a button (or
  // React re-runs the effect mid-action via StrictMode) we'd otherwise
  // double-fire approve/reject. Server-side webhook idempotency
  // handles the result correctly, but this avoids the noisy 409 toast
  // and the wasted round-trip.
  const decisionInFlight = useRef(false);

  // Mount logic: fetch preview, optionally chain into the action.
  useEffect(() => {
    if (!token) {
      setState('error');
      setErrorMessage(t('preview.missingToken') || 'Kein Token in der URL gefunden.');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await previewAPI.get(token);
        if (cancelled) return;
        setData(res.data);
        // Seed the editable working copy from the server snapshot.
        // Re-runs on action paths too, but those bail before reaching
        // the editor anyway.
        setEditedCaptions(res.data?.per_platform_captions || {});
        if (action === 'approve') {
          await runDecision('approve');
        } else if (action === 'reject') {
          await runDecision('reject');
        } else if (action === 'publish_now' || action === 'publish-now') {
          await runDecision('publish_now');
        } else {
          // Map server state → UI state so a user revisiting an
          // already-acted-on link doesn't re-prompt them.
          if (res.data?.posting_decision === 'approved') {
            setState('approved');
          } else if (res.data?.posting_decision === 'rejected' || res.data?.status === 'rejected') {
            setState('rejected');
          } else if (res.data?.status === 'completed') {
            // Already posted. Happens if user opens an old link.
            setState('approved');
          } else {
            setState('viewing');
          }
        }
      } catch (err) {
        if (cancelled) return;
        setState('error');
        setErrorMessage(
          err.response?.data?.detail ||
            t('preview.loadFailed') ||
            'Vorschau konnte nicht geladen werden. Der Link ist abgelaufen oder ungültig.'
        );
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, action]);

  // While a (re-)render is in flight, poll until the new video is ready and
  // then refresh the page data so the player swaps it in. Only runs in the
  // viewing state so it never fights the approve/reject flow.
  const isRendering = data?.video_status === 'rendering'
    || ['generating_video', 'pending', 'claiming'].includes(data?.status);
  useEffect(() => {
    if (!token || state !== 'viewing' || !isRendering) return undefined;
    let stop = false;
    const id = setInterval(async () => {
      try {
        const res = await previewAPI.get(token);
        if (stop) return;
        setData(res.data);
        if (res.data?.video_status !== 'rendering' && res.data?.video_url) {
          setEditedCaptions(res.data?.per_platform_captions || {});
        }
      } catch (_) { /* keep polling */ }
    }, 5000);
    return () => { stop = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, state, isRendering]);

  // Dirty detection. Only true after the user has interacted with
  // the editor AND the working copy diverges from the server snapshot.
  // The `userEditedRef.current` gate avoids the auto-action path
  // (mount-effect → setEditedCaptions → runDecision before state
  // flushes) triggering a phantom PATCH.
  const captionsDirty = (() => {
    if (!userEditedRef.current) return false;
    const server = data?.per_platform_captions || {};
    const keys = new Set([...Object.keys(server), ...Object.keys(editedCaptions)]);
    for (const k of keys) {
      if ((server[k] || '') !== (editedCaptions[k] || '')) return true;
    }
    return false;
  })();

  /** Manually persist caption edits via PATCH /preview/{token}.
   * Returns the updated data object (or null on failure). Toasts
   * on success/error. */
  const saveCaptions = async () => {
    if (!captionsDirty) return data;
    setSavingCaptions(true);
    try {
      const res = await previewAPI.patch(token, {
        per_platform_captions: editedCaptions,
      });
      setData(res.data);
      setEditedCaptions(res.data?.per_platform_captions || {});
      toast.success(t('autoPost.detail.saved') || 'Captions gespeichert.');
      return res.data;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && typeof detail === 'object' && detail.message)
        || t('autoPost.detail.saveFailed') || 'Speichern fehlgeschlagen.';
      toast.error(msg);
      return null;
    } finally {
      setSavingCaptions(false);
    }
  };

  /** Regenerate captions via the AI (token-authed). Replaces the
   * working copy + server data with fresh output. If the user has
   * unsaved edits we warn first since regen discards them. */
  const handleRegenerate = async () => {
    if (captionsDirty) {
      const proceed = window.confirm(
        t('autoPost.detail.regenerateWarn') ||
        'Du hast ungespeicherte Änderungen. Trotzdem neu generieren? (verwirft deine Bearbeitung)'
      );
      if (!proceed) return;
    }
    setRegenerating(true);
    try {
      const res = await previewAPI.regenerateCaptions(token);
      setData(res.data);
      setEditedCaptions(res.data?.per_platform_captions || {});
      toast.success(t('autoPost.detail.regenerated') || 'Captions neu generiert.');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && typeof detail === 'object' && detail.message)
        || t('autoPost.detail.regenerateFailed') || 'Neu generieren fehlgeschlagen.';
      toast.error(msg);
    } finally {
      setRegenerating(false);
    }
  };

  /** Seed the editor states from an options payload. */
  const seedEditors = (d) => {
    setPickImages(d.selected_images || []);
    setEditFields(Object.fromEntries((d.editable_fields || []).map((f) => [f.key, f.value || ''])));
    setEditVoiceover(d.voiceover || '');
  };

  /** Lazily load the templates/properties + current images, on-screen text
   * and voiceover so the user can edit them before re-rendering. */
  const loadOptions = async () => {
    if (options) return;
    try {
      const res = await previewAPI.getOptions(token);
      const d = res.data || {};
      setOptions(d);
      setPickTemplate(d.current_template_id || '');
      setPickProperty(d.current_property_id || '');
      setEditVoiceoverOn(d.voiceover_enabled !== false);
      // Pre-select the track that's actually in the current video; '' =
      // automatic for older items where we don't know it.
      const knownTrack = (d.music_tracks || []).some((m) => m.url === d.current_music_url);
      setPickMusic(knownTrack ? d.current_music_url : '');
      seedEditors(d);
    } catch (e) {
      toast.error(t('preview.optionsFailed') || 'Optionen konnten nicht geladen werden.');
    }
  };

  /** Play/pause the currently selected music track (preview only). */
  const stopMusicPreview = () => {
    if (musicAudioRef.current) musicAudioRef.current.pause();
    setMusicPlaying(false);
  };
  const toggleMusicPreview = () => {
    if (musicPlaying) { stopMusicPreview(); return; }
    const track = (options?.music_tracks || []).find((m) => m.url === pickMusic);
    if (!track?.url) return;
    if (musicAudioRef.current) musicAudioRef.current.pause();
    const a = new Audio(track.url);
    a.volume = 0.5;
    a.onended = () => setMusicPlaying(false);
    a.play().catch(() => {});
    musicAudioRef.current = a;
    setMusicPlaying(true);
  };
  // Stop playback on unmount so navigating away doesn't leave audio running.
  useEffect(() => () => { if (musicAudioRef.current) musicAudioRef.current.pause(); }, []);

  const toggleFastTrack = () => {
    setFastTrackOpen((v) => !v);
    if (!options) loadOptions();
  };

  /** The user switched property or template in the editor: refetch the
   * editing data for that combo (fresh image auto-pick + AI texts) so the
   * pickers below always match the selection. Discards stale responses via
   * a request counter — fast consecutive switches can't interleave. */
  const switchCombo = async (nextProperty, nextTemplate) => {
    setPickProperty(nextProperty);
    setPickTemplate(nextTemplate);
    const reqId = ++comboReqRef.current;
    setComboLoading(true);
    try {
      const res = await previewAPI.getOptions(token, {
        property_id: nextProperty || undefined,
        template_id: nextTemplate || undefined,
      });
      if (reqId !== comboReqRef.current) return; // stale — a newer switch won
      const d = res.data || {};
      // Keep the catalogue (templates/properties) from the first load; only
      // swap the editing payload + media metadata for the new combo.
      setOptions((cur) => ({ ...(cur || {}), ...d }));
      seedEditors(d);
    } catch (e) {
      if (reqId !== comboReqRef.current) return;
      const detail = e?.response?.data?.detail;
      toast.error(
        (typeof detail === 'string' && detail)
        || (t('preview.optionsFailed') || 'Optionen konnten nicht geladen werden.')
      );
    } finally {
      if (reqId === comboReqRef.current) setComboLoading(false);
    }
  };

  const toggleImage = (url) => {
    setPickImages((cur) => (cur.includes(url) ? cur.filter((u) => u !== url) : [...cur, url]));
  };

  /** Kick off a fresh render (#22/#23). `resetOptions` invalidates the cached
   * editing data (used after a property swap so the next open reloads fresh).
   * The polling effect below picks up the new video when it's ready. */
  const handleRerender = async (payload, { resetOptions = false } = {}) => {
    stopMusicPreview();
    setRerendering(true);
    try {
      const res = await previewAPI.rerender(token, payload);
      setData((d) => ({ ...(d || {}), ...res.data, video_url: null }));
      setFastTrackOpen(false);
      if (resetOptions) setOptions(null);
      toast.success(t('preview.rerenderStarted') || 'Neues Video wird erstellt …');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (typeof detail === 'string' && detail)
        || (detail && detail.message)
        || (t('preview.rerenderFailed') || 'Neu erstellen fehlgeschlagen.');
      toast.error(msg);
    } finally {
      setRerendering(false);
    }
  };

  const runDecision = async (which) => {
    // Re-entrancy guard: ref check + set is synchronous, so two
    // near-simultaneous callers (mount-effect + click) both see the
    // same pre-set value, and only the first one proceeds.
    if (decisionInFlight.current) return;
    decisionInFlight.current = true;
    setBusy(true);
    // A13. Preview-email engagement is the highest-signal event for
    // measuring whether the auto-post UX is working. Track both
    // approvals and rejections.
    track('preview_decision', { decision: which });
    try {
      // Auto-save unsaved caption edits before APPROVING or PUBLISHING
      // (NOT before rejecting. If they're rejecting they don't care
      // about the captions). This way the user doesn't have to click
      // Save → Approve as two separate actions; one click on
      // Bestätigen does both.
      if ((which === 'approve' || which === 'publish_now') && captionsDirty) {
        const saved = await saveCaptions();
        if (!saved) {
          // Save failed. Don't proceed with the decision. saveCaptions
          // already toasted the error.
          return;
        }
      }
      let res;
      if (which === 'approve') res = await previewAPI.approve(token);
      else if (which === 'reject') res = await previewAPI.reject(token);
      else res = await previewAPI.publishNow(token);

      // Backend returns 200 with `noop: true` + `noop_reason` when the
      // queue item is past the preview-pending window (already posted,
      // already rejected, render failed, slot+grace passed). Map each
      // reason to a distinct state so the UI shows real feedback
      // instead of a misleading green "approved" card.
      if (res.data?.noop) {
        const reason = res.data.noop_reason;
        if (reason === 'already_posted') setState('noop_already_posted');
        else if (reason === 'slot_passed') setState('noop_slot_passed');
        else if (reason === 'rejected_by_user') setState('noop_already_rejected');
        else if (reason === 'render_failed') setState('noop_render_failed');
        else setState('noop_already_rejected');  // sensible fallback
      } else if (which === 'publish_now') {
        setState('publishing_now');
      } else {
        setState(which === 'approve' ? 'approved' : 'rejected');
      }
    } catch (err) {
      if (err.response?.status === 409) {
        setState('conflict');
        setErrorMessage(err.response.data?.detail || '');
      } else {
        setState('error');
        setErrorMessage(
          err.response?.data?.detail ||
            t('preview.actionFailed') ||
            'Aktion fehlgeschlagen. Bitte später erneut versuchen.'
        );
      }
    } finally {
      setBusy(false);
      decisionInFlight.current = false;
    }
  };

  // Format the scheduled time in the user's locale. Server sends ISO; we
  // toLocaleString to whichever lang they're browsing in.
  const scheduledHuman = (() => {
    if (!data?.scheduled_for) return null;
    try {
      const d = new Date(data.scheduled_for);
      return d.toLocaleString(lang === 'de' ? 'de-AT' : 'en-US', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (_) {
      return data.scheduled_for;
    }
  })();

  return (
    <div className="min-h-screen bg-[#f7f3ea] px-4 py-8">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6 text-center">
          <div className="inline-flex p-3 bg-[#e9efe7] rounded-2xl mb-3">
            <Play className="w-7 h-7 text-[#1f4d3a]" />
          </div>
          <h1 className="text-2xl font-bold text-[#23211a]">
            {t('preview.title') || 'Auto-Post Vorschau'}
          </h1>
        </div>

        {state === 'loading' && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-[#1f4d3a] animate-spin" />
          </div>
        )}

        {state === 'error' && (
          <Card className="glass border-[#b3543f]/40">
            <CardContent className="p-6 text-center space-y-3">
              <XCircle className="w-10 h-10 text-[#b3543f] mx-auto" />
              <p className="text-sm text-[#a34430]">{errorMessage}</p>
            </CardContent>
          </Card>
        )}

        {state === 'conflict' && (
          <Card className="glass border-[#c7a869]">
            <CardContent className="p-6 text-center space-y-3">
              <AlertCircle className="w-10 h-10 text-[#9a6d1f] mx-auto" />
              <p className="text-sm text-[#9a6d1f]">{errorMessage}</p>
            </CardContent>
          </Card>
        )}

        {state === 'publishing_now' && (
          <Card className="glass border-[#1f4d3a]/40">
            <CardContent className="p-8 text-center space-y-3">
              <Play className="w-12 h-12 text-[#1f4d3a] mx-auto" />
              <h2 className="text-xl font-semibold text-[#2e6b50]">
                {t('preview.publishingNowTitle') || 'Wird sofort veröffentlicht'}
              </h2>
              <p className="text-sm text-[#4a463b] max-w-md mx-auto">
                {t('preview.publishingNowHelp') ||
                  'Wir posten dieses Video jetzt auf deinen verbundenen Plattformen. Den nächsten Auto-Post bekommst du wie gewohnt morgen per Email.'}
              </p>
            </CardContent>
          </Card>
        )}

        {state === 'approved' && (
          <Card className="glass border-[#1e7a4f]/40">
            <CardContent className="p-8 text-center space-y-3">
              <CheckCircle2 className="w-12 h-12 text-[#1e7a4f] mx-auto" />
              <h2 className="text-xl font-semibold text-[#1e7a4f]">
                {t('preview.approvedTitle') || 'Bestätigt. Wir posten zur geplanten Zeit'}
              </h2>
              {scheduledHuman && (
                <p className="text-sm text-[#6f6a5c]">
                  <Clock className="inline w-4 h-4 mr-1.5 -mt-0.5" />
                  {scheduledHuman}
                </p>
              )}
              <p className="text-xs text-[#8a8474] max-w-md mx-auto">
                {t('preview.approvedHelp') ||
                  'Du kannst dieses Tab jetzt schließen. Bei Problemen findest du den Status im Dashboard.'}
              </p>
            </CardContent>
          </Card>
        )}

        {state === 'rejected' && (
          <Card className="glass border-[#d8d0bd]">
            <CardContent className="p-8 text-center space-y-3">
              <XCircle className="w-12 h-12 text-[#6f6a5c] mx-auto" />
              <h2 className="text-xl font-semibold text-[#23211a]">
                {t('preview.rejectedTitle') || 'Post abgebrochen'}
              </h2>
              <p className="text-xs text-[#8a8474] max-w-md mx-auto">
                {t('preview.rejectedHelp') ||
                  'Wir überspringen diesen Slot. Morgen schicken wir dir einen neuen Vorschlag.'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Noop states. Clicked too late, slot already finalized.
            Each reason gets its own friendly message + an action so the
            user isn't left at a dead-end. */}
        {state === 'noop_already_posted' && (
          <Card className="glass border-[#1e7a4f]/40">
            <CardContent className="p-8 text-center space-y-3">
              <CheckCircle2 className="w-12 h-12 text-[#1e7a4f] mx-auto" />
              <h2 className="text-xl font-semibold text-[#1e7a4f]">
                {t('preview.alreadyPostedTitle') || 'Bereits gepostet'}
              </h2>
              <p className="text-sm text-[#6f6a5c]">
                {t('preview.alreadyPostedHelp') ||
                  'Dieses Video wurde schon veröffentlicht. Du musst nichts mehr tun. Den nächsten Auto-Post bekommst du wie gewohnt per Email.'}
              </p>
            </CardContent>
          </Card>
        )}

        {state === 'noop_slot_passed' && (
          <Card className="glass border-[#c7a869]">
            <CardContent className="p-8 text-center space-y-3">
              <Clock className="w-12 h-12 text-[#9a6d1f] mx-auto" />
              <h2 className="text-xl font-semibold text-[#9a6d1f]">
                {t('preview.slotPassedTitle') || 'Slot ist vorbei'}
              </h2>
              <p className="text-sm text-[#4a463b]">
                {t('preview.slotPassedHelp') ||
                  'Dieser geplante Slot ist abgelaufen, ohne dass eine Bestätigung gekommen ist. Wir haben den Post übersprungen. Der nächste kommt morgen.'}
              </p>
              <p className="text-xs text-[#8a8474] max-w-md mx-auto">
                {t('preview.slotPassedTip') ||
                  'Tipp: Wechsle in den Einstellungen auf „Automatisch mit Vorschau", damit verpasste Bestätigungen den Post nicht mehr stoppen.'}
              </p>
            </CardContent>
          </Card>
        )}

        {state === 'noop_already_rejected' && (
          <Card className="glass border-[#d8d0bd]">
            <CardContent className="p-8 text-center space-y-3">
              <XCircle className="w-12 h-12 text-[#6f6a5c] mx-auto" />
              <h2 className="text-xl font-semibold text-[#23211a]">
                {t('preview.alreadyRejectedTitle') || 'Bereits abgebrochen'}
              </h2>
              <p className="text-sm text-[#6f6a5c]">
                {t('preview.alreadyRejectedHelp') ||
                  'Du hast diesen Post schon vorher abgebrochen. Er wird nicht mehr veröffentlicht. Morgen kommt der nächste Vorschlag.'}
              </p>
            </CardContent>
          </Card>
        )}

        {state === 'noop_render_failed' && (
          <Card className="glass border-[#b3543f]/40">
            <CardContent className="p-8 text-center space-y-3">
              <AlertCircle className="w-12 h-12 text-[#b3543f] mx-auto" />
              <h2 className="text-xl font-semibold text-[#8c3a29]">
                {t('preview.renderFailedTitle') || 'Render fehlgeschlagen'}
              </h2>
              <p className="text-sm text-[#4a463b]">
                {t('preview.renderFailedHelp') ||
                  'Wir konnten dieses Video leider nicht erstellen. Wir versuchen es morgen mit einer anderen Immobilie. Falls das öfter passiert, melde dich bei support@real-content.at.'}
              </p>
            </CardContent>
          </Card>
        )}

        {state === 'viewing' && data && (
          <Card className="glass border-[#e3dccb]">
            <CardHeader>
              <CardTitle className="text-lg text-[#23211a]">
                {data.property_title || t('preview.untitled') || 'Deine Immobilie'}
              </CardTitle>
              {data.property_location && (
                <p className="text-xs text-[#6f6a5c]">{data.property_location}</p>
              )}
              {scheduledHuman && (
                <p className="text-xs text-[#1f4d3a] mt-1">
                  <Clock className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
                  {t('preview.scheduledFor', { date: scheduledHuman }) ||
                    `Geplant: ${scheduledHuman}`}
                </p>
              )}
            </CardHeader>
            <CardContent className="space-y-5">
              {isRendering ? (
                <div className="mx-auto w-full max-w-[280px] aspect-[9/16] rounded-xl overflow-hidden bg-black border border-[#e3dccb] flex flex-col items-center justify-center gap-3 text-center px-4">
                  <Loader2 className="w-8 h-8 text-[#1f4d3a] animate-spin" />
                  <p className="text-sm text-[#4a463b]">{t('preview.rerendering') || 'Neues Video wird erstellt …'}</p>
                  <p className="text-xs text-[#8a8474]">{t('preview.rerenderingHint') || 'Das dauert meist unter einer Minute.'}</p>
                </div>
              ) : data.video_url ? (
                <div className="relative mx-auto w-full max-w-[280px] aspect-[9/16] rounded-xl overflow-hidden bg-black border border-[#e3dccb]">
                  <video
                    src={data.video_url}
                    className="w-full h-full object-cover"
                    controls
                    playsInline
                  />
                </div>
              ) : (
                <div className="text-center text-[#8a8474] text-sm py-8">
                  {t('preview.videoUnavailable') ||
                    'Video noch nicht bereit. Versuch es in ein paar Minuten erneut.'}
                </div>
              )}

              {/* Per-platform editable captions. Same component as
                  the dashboard's queue detail modal. Lets the user
                  tweak each platform's variant before approving.
                  Edits auto-save when the user clicks Approve or
                  Publish Now (or via the explicit Save button when
                  they're dirty). */}
              {(data.per_platform_captions && Object.keys(data.per_platform_captions).length > 0) ? (
                <PerPlatformCaptionEditor
                  perPlatformCaptions={editedCaptions}
                  targetPlatforms={data.platforms || []}
                  onChange={(next) => {
                    userEditedRef.current = true;
                    setEditedCaptions(next);
                  }}
                  onRegenerate={handleRegenerate}
                  regenerating={regenerating}
                />
              ) : data.caption && (
                // Legacy fallback for items without per-platform
                // captions (older renders). Just the flat caption.
                <div className="p-3 rounded-lg bg-white border border-[#e3dccb]">
                  <p className="text-xs text-[#6f6a5c] mb-1.5">
                    {t('preview.captionLabel') || 'Caption'}
                  </p>
                  <p className="text-sm text-[#23211a] whitespace-pre-line">{data.caption}</p>
                </div>
              )}

              {/* Explicit Save button for caption edits. Visible only
                  when the user has dirty edits. They can save without
                  approving (e.g. "save my edits, I'll decide later")
                  or just rely on the auto-save when they click Approve. */}
              {captionsDirty && (
                <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-[#c7a869]/5 border border-[#c7a869]">
                  <p className="text-xs text-[#9a6d1f] leading-tight">
                    {data?.preview_mode === 'require_approval'
                      ? (t('preview.unsavedEdits') ||
                          'Du hast ungespeicherte Änderungen. „Freigeben" speichert automatisch.')
                      : (t('preview.unsavedEditsAuto') ||
                          'Du hast ungespeicherte Änderungen. Speichere sie, bevor das Video automatisch gepostet wird.')}
                  </p>
                  <Button
                    onClick={saveCaptions}
                    disabled={savingCaptions || busy}
                    size="sm"
                    variant="outline"
                    className="border-[#9a6d1f]/50 text-[#9a6d1f] hover:bg-[#f6edd8] shrink-0"
                  >
                    {savingCaptions ? (
                      <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                        {t('common.saving') || 'Speichere…'}</>
                    ) : (
                      <><Save className="w-3.5 h-3.5 mr-1.5" />
                        {t('common.save') || 'Speichern'}</>
                    )}
                  </Button>
                </div>
              )}

              {/* Fast-track edit (#22) / different property (#23). Lets the
                  recipient swap the template or pull a completely different
                  listing and re-render right from the phone, then approve. */}
              {!isRendering && (
                <div className="rounded-lg border border-[#e3dccb] bg-[#f2ecdf]">
                  <button
                    type="button"
                    onClick={toggleFastTrack}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left"
                  >
                    <span className="flex items-center gap-2 text-sm text-[#23211a]">
                      <Settings className="w-4 h-4 text-[#1f4d3a]" />
                      {t('preview.fastTrackTitle') || 'Passt nicht? Video anpassen'}
                    </span>
                    <span className="text-xs text-[#8a8474]">{fastTrackOpen ? '▲' : '▼'}</span>
                  </button>
                  {fastTrackOpen && (
                    <div className="px-3 pb-3 space-y-3 border-t border-[#e3dccb] pt-3">
                      {!options ? (
                        <div className="flex items-center gap-2 text-xs text-[#8a8474]">
                          <Loader2 className="w-4 h-4 animate-spin" /> {t('common.loading') || 'Lädt …'}
                        </div>
                      ) : (
                        <>
                          {/* Property — switch to a specific listing. Editors
                              below refresh for the new combo (fresh AI texts
                              + auto-picked images) before anything renders. */}
                          <div>
                            <label className="text-xs text-[#6f6a5c] mb-1 block">{t('preview.property') || 'Immobilie'}</label>
                            <select
                              value={pickProperty}
                              onChange={(e) => switchCombo(e.target.value, pickTemplate)}
                              disabled={rerendering || comboLoading}
                              className="w-full h-9 rounded-md bg-[#f7f3ea] border border-[#d8d0bd] text-[#23211a] px-2 text-sm disabled:opacity-60"
                              data-testid="preview-property-select"
                            >
                              {(options.properties || []).map((p) => {
                                const tpl = (options.templates || []).find((x) => x.id === pickTemplate);
                                const need = tpl?.media_count ?? options.media_count;
                                const tooFew = (p.image_count ?? 0) < need;
                                return (
                                  <option key={p.id} value={p.id} disabled={tooFew}>
                                    {p.title}{p.location ? ` · ${p.location}` : ''}{tooFew ? ` — ${t('preview.tooFewImages') || 'zu wenige Bilder'}` : ''}
                                  </option>
                                );
                              })}
                            </select>
                          </div>

                          {/* Template */}
                          <div>
                            <label className="text-xs text-[#6f6a5c] mb-1 block">{t('preview.template') || 'Vorlage'}</label>
                            <select
                              value={pickTemplate}
                              onChange={(e) => switchCombo(pickProperty, e.target.value)}
                              disabled={rerendering || comboLoading}
                              className="w-full h-9 rounded-md bg-[#f7f3ea] border border-[#d8d0bd] text-[#23211a] px-2 text-sm disabled:opacity-60"
                              data-testid="preview-template-select"
                            >
                              {(options.templates || []).map((tpl) => (
                                <option key={tpl.id} value={tpl.id}>{tpl.name} · {tpl.media_count} {t('preview.imagesShort') || 'Bilder'}</option>
                              ))}
                            </select>
                          </div>

                          {/* Editors refresh while the AI rewrites texts for a
                              newly picked property/template. */}
                          {comboLoading && (
                            <div className="flex items-center gap-2 text-xs text-[#1f4d3a] bg-[#e9efe7] border border-[#1f4d3a]/30 rounded-md px-3 py-2">
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              {t('preview.comboLoading') || 'Bilder & Texte werden angepasst …'}
                            </div>
                          )}

                          {/* Image picker — tap to (de)select; number = order */}
                          {!comboLoading && (options.property_images || []).length > 0 && (
                            <div>
                              <label className="text-xs text-[#6f6a5c] mb-1 block">
                                {t('preview.images') || 'Bilder'}{' '}
                                <span className={pickImages.length === options.media_count ? 'text-[#6f6a5c]' : 'text-[#9a6d1f] font-medium'}>
                                  ({pickImages.length}/{options.media_count})
                                </span>
                              </label>
                              <div className="grid grid-cols-4 gap-1.5">
                                {(options.property_images || []).map((url) => {
                                  const idx = pickImages.indexOf(url);
                                  const selected = idx !== -1;
                                  return (
                                    <button
                                      key={url}
                                      type="button"
                                      onClick={() => toggleImage(url)}
                                      className={`relative aspect-square rounded-md overflow-hidden border-2 transition ${selected ? 'border-[#1f4d3a]' : 'border-transparent opacity-60 hover:opacity-100'}`}
                                    >
                                      <img src={url} alt="" className="w-full h-full object-cover" />
                                      {selected && (
                                        <span className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-[#1f4d3a] text-white text-[10px] font-bold flex items-center justify-center">{idx + 1}</span>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                              <p className="text-[10px] text-[#8a8474] mt-1">
                                {pickImages.length === options.media_count
                                  ? (t('preview.imagesHint') || 'Reihenfolge = Reihenfolge im Video.')
                                  : (t('preview.imagesCountHint', { count: options.media_count }) || `Bitte genau ${options.media_count} Bilder wählen.`)}
                              </p>
                            </div>
                          )}

                          {/* On-screen text the AI wrote */}
                          {!comboLoading && (options.editable_fields || []).length > 0 && (
                            <div className="space-y-2">
                              <label className="text-xs text-[#6f6a5c] block">{t('preview.onScreenText') || 'Texte im Video'}</label>
                              {(options.editable_fields || []).map((f) => (
                                <div key={f.key}>
                                  <span className="text-[10px] text-[#8a8474] block mb-0.5">{f.label}</span>
                                  <textarea
                                    value={editFields[f.key] ?? ''}
                                    onChange={(e) => setEditFields((cur) => ({ ...cur, [f.key]: e.target.value }))}
                                    rows={1}
                                    className="w-full bg-[#f7f3ea] border border-[#d8d0bd] rounded-md px-2 py-1.5 text-sm text-[#23211a] resize-none focus:outline-none focus:ring-2 focus:ring-[#1f4d3a]/40"
                                  />
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Voiceover — per-render on/off + editable script */}
                          {!comboLoading && options.has_voiceover && (
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <label className="text-xs text-[#6f6a5c]">{t('preview.voiceoverScript') || 'Sprechtext (Voiceover)'}</label>
                                <div className="flex items-center gap-2">
                                  <span className="text-[10px] text-[#8a8474]">
                                    {editVoiceoverOn ? (t('preview.voiceoverOn') || 'An') : (t('preview.voiceoverOffLabel') || 'Aus')}
                                  </span>
                                  <Switch
                                    checked={editVoiceoverOn}
                                    onCheckedChange={setEditVoiceoverOn}
                                    data-testid="preview-voiceover-toggle"
                                  />
                                </div>
                              </div>
                              {editVoiceoverOn ? (
                                <textarea
                                  value={editVoiceover}
                                  onChange={(e) => setEditVoiceover(e.target.value)}
                                  rows={3}
                                  placeholder={t('preview.voiceoverPlaceholder') || 'Was die KI-Stimme sagt …'}
                                  className="w-full bg-[#f7f3ea] border border-[#d8d0bd] rounded-md px-2 py-1.5 text-sm text-[#23211a] resize-none focus:outline-none focus:ring-2 focus:ring-[#1f4d3a]/40"
                                  data-testid="preview-voiceover-edit"
                                />
                              ) : (
                                <p className="text-[11px] text-[#8a8474] bg-[#f7f3ea]/85 border border-[#e3dccb] rounded-md px-2.5 py-2">
                                  {t('preview.voiceoverOff') || 'Kein Voiceover — das Video bekommt nur Hintergrundmusik.'}
                                </p>
                              )}
                            </div>
                          )}

                          {/* Background music — a specific track or automatic
                              (random from the user's liked tracks). ▶ previews
                              the selected track before committing. */}
                          {!comboLoading && options.has_background_audio !== false && (options.music_tracks || []).length > 0 && (
                            <div>
                              <label className="text-xs text-[#6f6a5c] mb-1 block">{t('preview.music') || 'Musik'}</label>
                              <div className="flex gap-2">
                                <select
                                  value={pickMusic}
                                  onChange={(e) => { stopMusicPreview(); setPickMusic(e.target.value); }}
                                  disabled={rerendering}
                                  className="flex-1 h-9 rounded-md bg-[#f7f3ea] border border-[#d8d0bd] text-[#23211a] px-2 text-sm disabled:opacity-60"
                                  data-testid="preview-music-select"
                                >
                                  <option value="">{t('preview.musicAutomatic') || 'Automatisch (aus deinen Favoriten)'}</option>
                                  {(options.music_tracks || []).map((m) => (
                                    <option key={m.url} value={m.url}>{m.name}</option>
                                  ))}
                                </select>
                                <button
                                  type="button"
                                  onClick={toggleMusicPreview}
                                  disabled={!pickMusic}
                                  aria-label={musicPlaying ? 'Stop' : 'Anhören'}
                                  className={`w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 transition border ${
                                    musicPlaying
                                      ? 'bg-[#1f4d3a]/30 border-[#1f4d3a]/60 text-[#23211a]'
                                      : 'bg-[#fffdf7] border-[#d8d0bd] text-[#4a463b] hover:border-[#1f4d3a]/50 disabled:opacity-40'
                                  }`}
                                  data-testid="preview-music-preview"
                                >
                                  {musicPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Apply all edits → re-render. Sends the property
                              too when the user switched it, so one click
                              covers property + template + images + texts +
                              voiceover in a single fresh render. */}
                          <Button
                            onClick={() => handleRerender({
                              property_id: pickProperty && pickProperty !== options.current_property_id ? pickProperty : undefined,
                              template_id: pickTemplate || undefined,
                              selected_images: pickImages,
                              text_fields: editFields,
                              enable_voiceover: options.has_voiceover ? editVoiceoverOn : undefined,
                              voiceover_text: options.has_voiceover && editVoiceoverOn ? editVoiceover : undefined,
                              music_url: pickMusic || undefined,
                            }, { resetOptions: pickProperty !== options.current_property_id })}
                            disabled={rerendering || comboLoading || ((options.property_images || []).length > 0 && pickImages.length !== options.media_count)}
                            className="w-full bg-[#1f4d3a] hover:bg-[#1f4d3a] text-white disabled:opacity-50"
                            data-testid="preview-apply-edits"
                          >
                            {rerendering ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-3.5 h-3.5 mr-1.5" />{t('preview.applyEdits') || 'Übernehmen & neu erstellen'}</>}
                          </Button>

                          {/* Different property — fresh AI content for another listing */}
                          <div className="flex items-center justify-between gap-2 border-t border-[#e3dccb] pt-3">
                            <p className="text-xs text-[#6f6a5c]">
                              {t('preview.differentPropertyHint') || 'Diese Immobilie passt gar nicht?'}
                            </p>
                            <Button
                              onClick={() => handleRerender({ different_property: true }, { resetOptions: true })}
                              disabled={rerendering}
                              size="sm"
                              variant="outline"
                              className="border-[#c9c1ac] text-[#23211a] hover:bg-[#efe9db] shrink-0"
                              data-testid="preview-different-property"
                            >
                              {rerendering ? <Loader2 className="w-4 h-4 animate-spin" /> : (t('preview.differentProperty') || 'Andere Immobilie')}
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* v1.6. CTA layout flips on preview_mode.
                  In `require_approval` (strict mode) silence == skip, so
                  the green "Posten bestätigen" really is the primary
                  action and we keep the old layout.
                  In the default `auto_with_preview` silence == post –
                  showing a prominent "Bestätigen" button makes the user
                  think approval is required when it actually is a no-op
                  on the silence path. Drop the approve button entirely
                  in that mode; the user only needs "publish now" (if
                  they're impatient) or "stop" (if something looks wrong).
                  The help-line copy mirrors the mode so the user reads
                  one consistent story across banner + buttons + footer. */}
              {data?.preview_mode === 'require_approval' ? (
                <div className="flex flex-col sm:flex-row gap-2 pt-2">
                  <Button
                    onClick={() => runDecision('approve')}
                    disabled={busy}
                    className="flex-1 bg-[#1e7a4f] hover:bg-[#1e7a4f] text-white"
                  >
                    {busy ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <><CheckCircle2 className="w-4 h-4 mr-2" /> {t('preview.approve') || 'Posten freigeben'}</>
                    )}
                  </Button>
                  <Button
                    onClick={() => runDecision('publish_now')}
                    disabled={busy}
                    className="flex-1 bg-[#1f4d3a] hover:bg-[#1f4d3a] text-white"
                  >
                    {busy ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <><Play className="w-4 h-4 mr-2" /> {t('preview.publishNow') || 'Jetzt veröffentlichen'}</>
                    )}
                  </Button>
                  <Button
                    onClick={() => runDecision('reject')}
                    disabled={busy}
                    variant="outline"
                    className="flex-1 border-[#b3543f]/50 text-[#b3543f] hover:bg-[#f7e8e3]"
                  >
                    {busy ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <><XCircle className="w-4 h-4 mr-2" /> {t('preview.reject') || 'Diesen Post abbrechen'}</>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-2 pt-2">
                  <Button
                    onClick={() => runDecision('publish_now')}
                    disabled={busy}
                    className="flex-1 bg-[#1f4d3a] hover:bg-[#1f4d3a] text-white"
                  >
                    {busy ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <><Play className="w-4 h-4 mr-2" /> {t('preview.publishNow') || 'Jetzt veröffentlichen'}</>
                    )}
                  </Button>
                  <Button
                    onClick={() => runDecision('reject')}
                    disabled={busy}
                    variant="outline"
                    className="flex-1 border-[#b3543f]/50 text-[#b3543f] hover:bg-[#f7e8e3]"
                  >
                    {busy ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <><XCircle className="w-4 h-4 mr-2" /> {t('preview.stop') || 'Diesen Post stoppen'}</>
                    )}
                  </Button>
                </div>
              )}
              <p className="text-xs text-[#8a8474] text-center">
                {data?.preview_mode === 'require_approval'
                  ? (t('preview.noActionHelpStrict')
                      || 'Ohne deine Freigabe wird dieser Post nicht veröffentlicht.')
                  : (t('preview.noActionHelp')
                      || 'Wenn du nichts machst, posten wir das Video automatisch zur geplanten Zeit.')}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Navigation footer. Appears on every state EXCEPT loading.
            The user lands here from an email link; without this they're
            stranded with no way back to the app. For terminal states
            (approved/rejected/noop_*) this is the only forward path.
            Logged-out users (different browser, mobile email client)
            get a single "sign in" button. */}
        {state !== 'loading' && (
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 text-sm">
            {loggedIn ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/dashboard')}
                  className="border-[#d8d0bd] text-[#4a463b] hover:bg-[#efe9db] w-full sm:w-auto"
                >
                  <LayoutDashboard className="w-4 h-4 mr-1.5" />
                  {t('preview.backToDashboard') || 'Zum Dashboard'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/auto-post')}
                  className="border-[#d8d0bd] text-[#4a463b] hover:bg-[#efe9db] w-full sm:w-auto"
                >
                  <Settings className="w-4 h-4 mr-1.5" />
                  {t('preview.manageAutoPost') || 'Auto-Post verwalten'}
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/auth')}
                className="border-[#d8d0bd] text-[#4a463b] hover:bg-[#efe9db]"
              >
                <LogIn className="w-4 h-4 mr-1.5" />
                {t('preview.signIn') || 'Bei Real Content anmelden'}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
